import React, { useState } from 'react';
import type { Venue } from '../../../types/venue';
import { updateVenue } from '../../../utils/venueApi';
import Button from '../../ui/Button';

interface PunchCardSettingsPanelProps {
  venue: Venue;
  onUpdate: (venue: Venue) => void;
}

const inputBase =
  'w-full px-3 py-2.5 border border-coffee-200 rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-coffee-400 transition-colors';

const PunchCardSettingsPanel: React.FC<PunchCardSettingsPanelProps> = ({ venue, onUpdate }) => {
  const [totalPunches, setTotalPunches] = useState(venue.punchCard?.totalPunches ?? 10);
  const [rewardText, setRewardText] = useState(venue.punchCard?.rewardText ?? '');
  const [expiryDays, setExpiryDays] = useState<string>(venue.punchCard?.expiryDays?.toString() ?? '');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const updated = await updateVenue(venue.id, {
        punchCard: {
          enabled: venue.punchCard?.enabled ?? true,
          totalPunches,
          rewardText,
          expiryDays: expiryDays ? parseInt(expiryDays, 10) : undefined,
        },
      } as any);
      onUpdate(updated);
      setFeedback({ type: 'success', message: 'Punch card settings saved.' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Punch Card</div>
        <h2 className="text-display text-2xl text-coffee-900">Punch card settings</h2>
      </div>

      <div className="bg-white border border-coffee-100 rounded-2xl p-6 space-y-5">
        <div>
          <label htmlFor="total-punches" className="block text-sm font-medium text-coffee-800 mb-1.5">
            Punches required for reward
          </label>
          <input
            id="total-punches"
            type="number"
            min={3}
            max={20}
            value={totalPunches}
            onChange={(e) => setTotalPunches(parseInt(e.target.value, 10) || 10)}
            className={inputBase}
          />
        </div>

        <div>
          <label htmlFor="reward-text" className="block text-sm font-medium text-coffee-800 mb-1.5">
            Reward text
          </label>
          <input
            id="reward-text"
            type="text"
            value={rewardText}
            onChange={(e) => setRewardText(e.target.value)}
            placeholder="e.g., Free coffee or pastry of your choice"
            className={inputBase}
          />
        </div>

        <div>
          <label htmlFor="expiry-days" className="block text-sm font-medium text-coffee-800 mb-1.5">
            Expiry{' '}
            <span className="text-coffee-400 text-xs font-normal">(days — leave blank for no expiry)</span>
          </label>
          <input
            id="expiry-days"
            type="number"
            min={1}
            value={expiryDays}
            onChange={(e) => setExpiryDays(e.target.value)}
            placeholder="No expiry"
            className={inputBase}
          />
        </div>
      </div>

      {feedback && (
        <div className={`text-sm rounded-xl p-4 border ${
          feedback.type === 'success'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {feedback.message}
        </div>
      )}

      <Button variant="primary" onClick={handleSave} loading={saving}>
        {saving ? 'Saving...' : 'Save settings'}
      </Button>
    </div>
  );
};

export default PunchCardSettingsPanel;
